import React, { useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

import {
  Grid,
  Stack,
  Avatar,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Menu,
  MenuList,
  MenuItem,
  ButtonBase,
  AppBar
} from '@mui/material';

import { alpha } from '@mui/material/styles';

import {
  MenuTwoTone,
  ExpandMore,
  ExitToApp,
  Key,
  Person2Outlined,
  SettingsOutlined,
  LightMode,
  DarkMode
} from '@mui/icons-material';

import { logout } from '../../action/auth';
import { SidebarContext } from './contexts/SidebarContext';
import { useTheme } from '../../context/themeContext';

const NavBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const { mode, toggleTheme } = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);

  const { user } = useSelector((state) => state.user);
  const { authData } = useSelector((state) => state.auth);
  const userInfo = authData ? authData?.data : user;

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    dispatch(logout(navigate));
    handleMenuClose();
  };

  const UserMenu = () => (
    <MenuList
      sx={{
        p: 1,
        '& .MuiMenuItem-root': {
          borderRadius: 2,
        },
      }}
    >
      <Typography variant="subtitle2" sx={{ px: 1, py: 0.5 }}>
        Settings
      </Typography>

      <Divider sx={{ my: 1 }} />

      <MenuItem
        component={RouterLink}
        to="/user/page/profile?section=account"
        onClick={handleMenuClose}
      >
        <Person2Outlined fontSize="small" />
        <Typography sx={{ ml: 1 }}>Account</Typography>
      </MenuItem>

      <MenuItem
        component={RouterLink}
        to="/user/page/profile?section=profile"
        onClick={handleMenuClose}
      >
        <SettingsOutlined fontSize="small" />
        <Typography sx={{ ml: 1 }}>Profile</Typography>
      </MenuItem>

      <MenuItem
        component={RouterLink}
        to="/user/page/profile?section=security"
        onClick={handleMenuClose}
      >
        <Key fontSize="small" />
        <Typography sx={{ ml: 1 }}>Change Password</Typography>
      </MenuItem>

      <Divider sx={{ my: 1 }} />

      <MenuItem onClick={handleLogout}>
        <ExitToApp fontSize="small" />
        <Typography sx={{ ml: 1 }}>Logout</Typography>
      </MenuItem>
    </MenuList>
  );

  return (
    <AppBar position="sticky" sx={{px:2,boxShadow:1}}>
      <Grid
        container
        alignItems="center"
        sx={{
          py: 1,
          px: 1,
        }}
      >
        {/* LEFT SECTION */}
        <Grid size={{xs:6,md:3}}>
          {!sidebarToggle && (
            <Tooltip title="Open Menu">
              <IconButton sx={{ color: 'white' }} onClick={toggleSidebar}>
                <MenuTwoTone />
              </IconButton>
            </Tooltip>
          )}
        </Grid>

        {/* RIGHT SECTION */}
        <Grid size={{xs:6,md:9}}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            pr: 2,
          }}
        >
          {/* THEME TOGGLE */}
          <Tooltip title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}>
            <IconButton
              onClick={toggleTheme}
              sx={{
                color: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'rotate(180deg)',
                  bgcolor: (theme) => alpha(theme.palette.common.white, 0.15),
                },
              }}
            >
              {mode === 'light' ? <DarkMode /> : <LightMode />}
            </IconButton>
          </Tooltip>

          {/* USER MENU BUTTON */}
          <ButtonBase
            onClick={handleMenuOpen}
            sx={{
              borderRadius: '25px',
              px: 1,
              transition: 'background 0.2s ease',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.4),
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                src={userInfo?.pic}
                sx={{
                  width: 26,
                  height: 26,
                  boxShadow: (theme) =>
                    `0 0 0 3px ${theme.palette.background.paper},
                    0 0 0 4px ${theme.palette.primary.main}`,
                }}
              />
              <Typography
                sx={{ color: 'white', fontWeight: 500 }}
                display={{ xs: 'none', sm: 'block' }}
              >
                {userInfo?.username}
              </Typography>
              <ExpandMore sx={{ color: 'white' }} />
            </Stack>
          </ButtonBase>

          {/* USER MENU */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            elevation={24}
          >
            <UserMenu />
          </Menu>
        </Grid>
      </Grid>
    </AppBar>
  );
};

export default NavBar;